/**
 * Inline search suggestions for the Meddey header search bar.
 *
 * Replaces the search sidebar drawer with a dropdown anchored under the input.
 * All markup comes from Liquid (sections/meddey-search-suggest.liquid and
 * snippets/meddey-search-panel.liquid) — this file only fetches, re-orders DOM
 * nodes and wires keyboard/focus behaviour, so there is no JS product renderer
 * to keep in sync with the theme's styles.
 */
(() => {
  const SUGGEST_SECTION = 'meddey-search-suggest';
  const RECENT_KEY = 'theme:recent-searches';
  const VIEWED_KEY = 'theme:recently-viewed-products';
  const SIGNALS_KEY = 'theme:browse-signals';
  const MAX_RECENT = 6;

  const routes = (window.themeVariables && window.themeVariables.routes) || {
    searchUrl: '/search',
    predictiveSearchUrl: '/search/suggest'
  };

  const strings = (window.themeVariables && window.themeVariables.strings) || {};

  const readJSON = (key, fallback) => {
    try {
      return JSON.parse(localStorage.getItem(key)) || fallback;
    } catch (e) {
      return fallback;
    }
  };

  const writeJSON = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* Safari private mode — suggestions still work, they just are not remembered */
    }
  };

  const tokenize = (value) =>
    (value || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
      .split(/\s+/)
      .filter(Boolean);

  const supportsPredictiveApi = () => {
    const node = document.getElementById('shopify-features');

    if (!node) {
      return true;
    }

    try {
      return JSON.parse(node.textContent).predictiveSearch !== false;
    } catch (e) {
      return true;
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Ranking algorithms                                                     */
  /* ---------------------------------------------------------------------- */

  /**
   * Lexical match strength of one suggestion against the typed tokens.
   * Exact title beats a title prefix, which beats a word prefix, which beats a
   * loose substring. Attribute hits (vendor/type/tags) count for less.
   */
  const textScore = (item, tokens, rawQuery) => {
    if (tokens.length === 0) {
      return 0;
    }

    const title = item.title;
    let score = 0;

    if (title === rawQuery) {
      score += 100;
    } else if (title.startsWith(rawQuery)) {
      score += 60;
    }

    const words = title.split(/\s+/);

    tokens.forEach((token) => {
      if (words.some((word) => word === token)) {
        score += 34;
      } else if (words.some((word) => word.startsWith(token))) {
        score += 24;
      } else if (title.includes(token)) {
        score += 12;
      } else if (item.vendor.includes(token) || item.type.includes(token)) {
        score += 8;
      } else if (item.tags.includes(token)) {
        score += 4;
      }
    });

    // Shorter titles are usually the more specific match for the same hits.
    score += Math.max(0, 12 - title.length / 12);

    return score / tokens.length;
  };

  /** 0..1 — how close this product is to what the shopper has been browsing. */
  const affinityScore = (item, signals) => {
    const max = signals.max;

    if (!max) {
      return 0;
    }

    const vendor = (signals.vendors[item.vendor] || 0) / max;
    const type = (signals.types[item.type] || 0) / max;
    const collection = item.collections.reduce(
      (best, handle) => Math.max(best, (signals.collections[handle] || 0) / max),
      0
    );
    const viewed = signals.viewed.includes(item.id) ? 1 : 0;

    return Math.min(1, vendor * 0.3 + type * 0.3 + collection * 0.25 + viewed * 0.15);
  };

  const ALGORITHMS = {
    /* Server order wins — Shopify already ranked these. */
    relevance: () => 0,

    best_match: (item, ctx) => textScore(item, ctx.tokens, ctx.query),

    smart: (item, ctx) =>
      textScore(item, ctx.tokens, ctx.query) +
      affinityScore(item, ctx.signals) * 60 * ctx.strength +
      (item.available ? 6 : 0) +
      (item.pinnedRank ? 20 : 0),

    curated: (item) => (item.pinnedRank ? 1000 - item.pinnedRank : 0),

    in_stock: (item) => (item.available ? 1 : 0),

    price_asc: (item) => -item.price,

    price_desc: (item) => item.price,

    discount: (item) => item.discount
  };

  const readItem = (node) => ({
    node,
    id: parseInt(node.dataset.id, 10) || 0,
    title: node.dataset.title || '',
    vendor: node.dataset.vendor || '',
    type: node.dataset.type || '',
    tags: node.dataset.tags || '',
    collections: (node.dataset.collections || '').split(' ').filter(Boolean),
    price: parseInt(node.dataset.price, 10) || 0,
    discount: parseInt(node.dataset.discount, 10) || 0,
    available: node.dataset.available === 'true',
    pinnedRank: parseInt(node.dataset.pinnedRank, 10) || 0
  });

  /* ---------------------------------------------------------------------- */
  /* Element                                                                */
  /* ---------------------------------------------------------------------- */

  class MeddeySearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-suggest-input]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-suggest-panel]');

      if (!this.input || !this.panel) {
        return;
      }

      this.resultsElement = this.panel.querySelector('[data-suggest-results]');
      this.defaultElement = this.panel.querySelector('[data-suggest-default]');
      this.loadingElement = this.panel.querySelector('[data-suggest-loading]');
      this.footerElement = this.panel.querySelector('[data-suggest-footer]');
      this.viewAllElement = this.panel.querySelector('[data-suggest-view-all]');

      this.algorithm = this.dataset.algorithm || 'smart';
      this.personalize = this.dataset.personalize === 'true';
      this.strength = (parseInt(this.dataset.strength, 10) || 0) / 100;
      this.limit = parseInt(this.dataset.limit, 10) || 6;
      this.recentLimit = parseInt(this.dataset.recentLimit, 10) || 2;
      this.rules = this._parseRules();
      this.cache = new Map();
      this.activeIndex = -1;
      this.defaultsLoaded = false;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.panel.id);
      this.panel.setAttribute('role', 'listbox');

      this._onInput = this._debounce(this._search.bind(this), 180);

      this.input.addEventListener('focus', () => this._open());
      this.input.addEventListener('click', () => this._open());
      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('keydown', this._onKeydown.bind(this));
      this.form && this.form.addEventListener('submit', this._onSubmit.bind(this));
      this.panel.addEventListener('mousedown', (event) => {
        // Keep focus on the input so the panel does not close before the click lands.
        if (!event.target.closest('a')) {
          event.preventDefault();
        }
      });

      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this._close();
        }
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) {
          this._close();
        }
      });

      this.panel.addEventListener('click', (event) => {
        const clearButton = event.target.closest('[data-clear-recent]');

        if (clearButton) {
          writeJSON(RECENT_KEY, []);
          this._renderRecentSearches();
        }
      });

      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));

      idle(() => {
        this._renderRecentSearches();
        this._renderAlgorithmSwitcher();
        this._setupTypingPlaceholder();
      });
    }

    /**
     * Types through the merchant's trending terms in the placeholder.
     * Purely decorative: it never touches the accessible name (aria-label stays
     * fixed), pauses on focus, while typing, and when the tab is hidden, and is
     * skipped entirely for prefers-reduced-motion.
     */
    _setupTypingPlaceholder() {
      if (this.dataset.typing !== 'true') {
        return;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      const terms = Array.from(this.panel.querySelectorAll('[data-trending-term]'))
        .map((node) => node.textContent.trim())
        .filter(Boolean);

      if (terms.length === 0) {
        return;
      }

      const idlePlaceholder = this.input.getAttribute('placeholder') || '';
      const prefix = this.dataset.typingPrefix || '';
      let termIndex = 0;
      let chars = 0;
      let deleting = false;
      let timer = null;

      const schedule = (delay) => {
        clearTimeout(timer);
        timer = setTimeout(tick, delay);
      };

      const pause = () => {
        clearTimeout(timer);
        timer = null;
        chars = 0;
        deleting = false;
        this.input.setAttribute('placeholder', idlePlaceholder);
      };

      const tick = () => {
        // The shopper started typing between frames.
        if (document.hidden || document.activeElement === this.input || this.input.value !== '') {
          pause();
          return;
        }

        const term = terms[termIndex % terms.length];

        this.input.setAttribute('placeholder', prefix + term.slice(0, chars));

        if (!deleting && chars < term.length) {
          chars += 1;
          schedule(60);
        } else if (!deleting) {
          deleting = true;
          schedule(1600);
        } else if (chars > 0) {
          chars -= 1;
          schedule(28);
        } else {
          deleting = false;
          termIndex += 1;
          schedule(320);
        }
      };

      // No polling: the animation only runs while the box is genuinely idle.
      this.input.addEventListener('focus', pause);
      this.input.addEventListener('blur', () => schedule(1200));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          pause();
        } else {
          schedule(800);
        }
      });

      schedule(1200);
    }

    /* -- open / close -- */

    _open() {
      if (this.panel.hidden === false) {
        return;
      }

      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');

      if (!this.defaultsLoaded) {
        this.defaultsLoaded = true;
        this._loadRecentlyViewed();
        this._loadCollectionSuggestions();
      }

      if (this.input.value.trim() !== '') {
        this._search();
      }
    }

    _close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this._setActive(-1);
    }

    /* -- merchant rules -- */

    _parseRules() {
      const config = document.getElementById('meddey-search-rules');
      const parsed = { synonyms: [], redirects: [] };

      if (!config) {
        return parsed;
      }

      try {
        const raw = JSON.parse(config.textContent);

        parsed.synonyms = (raw.synonyms || []).map((group) =>
          group.map((term) => term.trim().toLowerCase()).filter(Boolean)
        );
        parsed.redirects = (raw.redirects || []).map((rule) => ({
          keyword: (rule.keyword || '').trim().toLowerCase(),
          url: rule.url
        }));
      } catch (e) {
        /* Malformed merchant input must never break the search box */
      }

      return parsed;
    }

    _redirectFor(query) {
      const match = this.rules.redirects.find((rule) => rule.keyword && rule.keyword === query);
      return match ? match.url : null;
    }

    /** Canonical alternative term for a query, used only when a search returns nothing. */
    _synonymFor(query) {
      const group = this.rules.synonyms.find((terms) => terms.includes(query));

      if (!group) {
        return null;
      }

      return group.find((term) => term !== query) || null;
    }

    /* -- search -- */

    async _search() {
      const query = this.input.value.trim();
      const requestId = this.searchRequestId = (this.searchRequestId || 0) + 1;
      if (this.abortController) this.abortController.abort();

      this._updateViewAll(query);

      if (query === '') {
        this.resultsElement.hidden = true;
        this.resultsElement.innerHTML = '';
        this.defaultElement.hidden = false;
        if (this.algosElement) this.algosElement.hidden = true;
        this.footerElement.hidden = true;
        this.loadingElement.hidden = true;
        this._setActive(-1);
        return;
      }

      this.defaultElement.hidden = true;
      this.loadingElement.hidden = false;

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      let html = this.cache.get(query.toLowerCase());

      if (html === undefined) {
        try {
          html = await this._fetchSuggestions(query, this.abortController.signal);
        } catch (e) {
          if (e.name === 'AbortError') {
            return;
          }

          this.loadingElement.hidden = true;
          return;
        }

          this.cache.set(query.toLowerCase(), html);
      }

      if (requestId !== this.searchRequestId || this.input.value.trim() !== query) return;
      this.loadingElement.hidden = true;
      this.resultsElement.innerHTML = html;
      this.resultsElement.hidden = false;
      this.footerElement.hidden = false;
      if (this.algosElement) this.algosElement.hidden = false;

      this._applyBoostRules(query);
      this._rank(query);
      this._setActive(-1);
    }

    async _fetchSuggestions(query, signal) {
      let payload = await this._requestPayload(query, signal);

      if (payload && payload.dataset.hasResults === 'false') {
        const synonym = this._synonymFor(query.toLowerCase());

        if (synonym) {
          const alternative = await this._requestPayload(synonym, signal);

          if (alternative && alternative.dataset.hasResults === 'true') {
            payload = alternative;
          }
        }
      }

      return payload ? payload.innerHTML : '';
    }

    async _requestPayload(query, signal) {
      const url = supportsPredictiveApi()
        ? `${routes.predictiveSearchUrl}?q=${encodeURIComponent(query)}` +
          `&resources[type]=${this._resourceTypes()}&resources[limit]=10&resources[limit_scope]=each` +
          `&section_id=${SUGGEST_SECTION}`
        : `${routes.searchUrl}?q=${encodeURIComponent(query)}*&type=product&section_id=${SUGGEST_SECTION}`;

      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Search is temporarily unavailable');
      const holder = document.createElement('div');

      holder.innerHTML = await response.text();

      return holder.querySelector('[data-suggest-payload]');
    }

    _resourceTypes() {
      const types = ['product'];

      if (this.dataset.showQueries === 'true') types.push('query');
      if (this.dataset.showCollections === 'true') types.push('collection');
      if (this.dataset.showArticles === 'true') types.push('article');
      if (this.dataset.showPages === 'true') types.push('page');

      return types.join(',');
    }

    /* -- ranking -- */

    _rank(query) {
      const list = this.resultsElement.querySelector('[data-suggest-products]');

      if (!list) {
        return;
      }

      const algorithm = ALGORITHMS[this.algorithm] || ALGORITHMS.smart;
      const ctx = {
        query: query.toLowerCase(),
        tokens: tokenize(query),
        signals: this._signals(),
        strength: this.personalize ? this.strength : 0
      };

      const items = Array.from(list.querySelectorAll('[data-suggest-item]')).map((node, index) => {
        const item = readItem(node);
        return { item, index, score: algorithm(item, ctx) };
      });

      // Stable: equal scores keep Shopify's own relevance order.
      items.sort((a, b) => b.score - a.score || a.index - b.index);

      const fragment = document.createDocumentFragment();

      items.slice(0, this.limit).forEach((entry) => {
        entry.item.node.hidden = false;
        entry.item.node.style.display = '';
        fragment.appendChild(entry.item.node);
      });
      items.slice(this.limit).forEach((entry) => {
        entry.item.node.hidden = true;
        entry.item.node.style.display = 'none';
        fragment.appendChild(entry.item.node);
      });

      list.appendChild(fragment);
    }

    _applyBoostRules(query) {
      const list = this.resultsElement.querySelector('[data-suggest-products]');

      if (!list) {
        return;
      }

      const lowered = query.toLowerCase();
      const seen = new Set(
        Array.from(list.querySelectorAll('[data-suggest-item]')).map((node) => node.dataset.id)
      );

      document.querySelectorAll('[data-boost-rule]').forEach((template) => {
        const keywords = (template.dataset.boostKeywords || '')
          .split(',')
          .map((keyword) => keyword.trim())
          .filter(Boolean);

        const matches = keywords.some(
          (keyword) => lowered.startsWith(keyword) || keyword.startsWith(lowered)
        );

        if (!matches) {
          return;
        }

        Array.from(template.content.querySelectorAll('[data-suggest-item]')).forEach((node) => {
          if (seen.has(node.dataset.id)) {
            return;
          }

          seen.add(node.dataset.id);

          const clone = node.cloneNode(true);

          clone.dataset.pinnedRank = clone.dataset.pinnedRank || '1';
          list.prepend(clone);
        });
      });
    }

    /** Aggregated browsing signals, normalised so affinityScore can stay 0..1. */
    _signals() {
      const stored = readJSON(SIGNALS_KEY, {});
      const signals = {
        vendors: stored.vendors || {},
        types: stored.types || {},
        collections: stored.collections || {},
        viewed: readJSON(VIEWED_KEY, []),
        max: 1
      };

      const counts = [
        ...Object.values(signals.vendors),
        ...Object.values(signals.types),
        ...Object.values(signals.collections)
      ];

      signals.max = counts.length > 0 ? Math.max(...counts) : 1;

      return signals;
    }

    /* -- default panel -- */

    async _loadRecentlyViewed() {
      const mount = this.panel.querySelector('[data-recently-viewed]');
      const ids = readJSON(VIEWED_KEY, []).slice(0, this.recentLimit);

      if (!mount || ids.length === 0) {
        return;
      }

      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const html = await this._loadInto(
        `${routes.searchUrl}?q=${encodeURIComponent(query)}&type=product&section_id=${SUGGEST_SECTION}`
      );

      if (!html) {
        return;
      }

      mount.innerHTML = html;
      this._retitle(mount, strings.searchRecentlyViewed || 'Recently viewed');

      // The section endpoint returns up to the suggestion limit; trim to the
      // short "last few" list so curated product suggestions stay in view.
      Array.from(mount.querySelectorAll('[data-suggest-item]'))
        .slice(this.recentLimit)
        .forEach((node) => node.remove());

      mount.hidden = false;
    }

    async _loadCollectionSuggestions() {
      const mount = this.panel.querySelector('[data-collection-suggestions]');
      const signals = readJSON(SIGNALS_KEY, {});
      const handle = signals.lastCollection;

      if (!mount || !handle) {
        return;
      }

      const html = await this._loadInto(
        `${routes.rootUrlWithoutSlash || ''}/collections/${encodeURIComponent(handle)}?section_id=${SUGGEST_SECTION}`
      );

      if (!html) {
        return;
      }

      mount.innerHTML = html;
      mount.hidden = false;
    }

    async _loadInto(url) {
      try {
        const response = await fetch(url);
        const holder = document.createElement('div');

        holder.innerHTML = await response.text();

        const payload = holder.querySelector('[data-suggest-payload]');

        return payload && payload.dataset.hasResults === 'true' ? payload.innerHTML : '';
      } catch (e) {
        return '';
      }
    }

    _retitle(mount, title) {
      const heading = mount.querySelector('.meddey-suggest__group-title');

      if (heading) {
        heading.textContent = title;
      }
    }

    _renderRecentSearches() {
      const group = this.panel.querySelector('[data-recent-searches]');
      const list = this.panel.querySelector('[data-recent-list]');

      if (!group || !list) {
        return;
      }

      const terms = readJSON(RECENT_KEY, []);

      if (terms.length === 0) {
        group.hidden = true;
        return;
      }

      list.innerHTML = terms
        .map(
          (term) =>
            `<li role="option"><a class="meddey-suggest__chip" data-suggest-link href="${
              routes.searchUrl
            }?q=${encodeURIComponent(term)}&type=product">${term.replace(/[<>&"]/g, '')}</a></li>`
        )
        .join('');

      group.hidden = false;
    }

    _renderAlgorithmSwitcher() {
      if (this.dataset.switcher !== 'true') {
        return;
      }

      const labels = {
        smart: 'For you',
        relevance: 'Relevance',
        best_match: 'Best match',
        in_stock: 'In stock',
        price_asc: 'Price ↑',
        price_desc: 'Price ↓',
        discount: 'Best deal'
      };

      const nav = document.createElement('div');

      nav.className = 'meddey-suggest__algos';
      nav.setAttribute('role', 'group');
      nav.setAttribute('aria-label', 'Sort suggestions');
      nav.innerHTML = Object.keys(labels)
        .map(
          (key) =>
            `<button type="button" class="meddey-suggest__algo${
              key === this.algorithm ? ' is-active' : ''
            }" data-algo="${key}">${labels[key]}</button>`
        )
        .join('');

      nav.addEventListener('click', (event) => {
        const button = event.target.closest('[data-algo]');

        if (!button) {
          return;
        }

        this.algorithm = button.dataset.algo;
        nav.querySelectorAll('[data-algo]').forEach((node) =>
          node.classList.toggle('is-active', node === button)
        );
        this._rank(this.input.value.trim());
      });

      this.resultsElement.before(nav);
      this.algosElement = nav;
      nav.hidden = true;
    }

    /* -- keyboard & submit -- */

    _links() {
      const scope = this.resultsElement.hidden ? this.defaultElement : this.resultsElement;
      return Array.from(scope.querySelectorAll('[data-suggest-link]')).filter(
        (link) => link.offsetParent !== null
      );
    }

    _setActive(index) {
      const links = this._links();

      links.forEach((link) => link.classList.remove('is-active'));
      this.activeIndex = index;

      if (index >= 0 && links[index]) {
        links[index].classList.add('is-active');
        links[index].scrollIntoView({ block: 'nearest' });
      }
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        this._close();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') {
        return;
      }

      if (this.panel.hidden) {
        if (event.key === 'ArrowDown') {
          this._open();
        }
        return;
      }

      const links = this._links();

      if (event.key === 'Enter') {
        if (this.activeIndex >= 0 && links[this.activeIndex]) {
          event.preventDefault();
          this._rememberTerm(this.input.value.trim());
          links[this.activeIndex].click();
        }
        return;
      }

      if (links.length === 0) {
        return;
      }

      event.preventDefault();

      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (this.activeIndex + step + links.length + 1) % (links.length + 1);

      this._setActive(next === links.length ? -1 : next);
    }

    _onSubmit(event) {
      const query = this.input.value.trim();

      this._rememberTerm(query);

      const redirect = this._redirectFor(query.toLowerCase());

      if (redirect) {
        event.preventDefault();
        window.location.href = redirect;
      }
    }

    _rememberTerm(term) {
      if (!term) {
        return;
      }

      const terms = readJSON(RECENT_KEY, []).filter(
        (existing) => existing.toLowerCase() !== term.toLowerCase()
      );

      terms.unshift(term);
      writeJSON(RECENT_KEY, terms.slice(0, MAX_RECENT));
    }

    _updateViewAll(query) {
      if (!this.viewAllElement) {
        return;
      }

      const sortBy = this.dataset.sortBy ? `&sort_by=${this.dataset.sortBy}` : '';

      this.viewAllElement.href = `${routes.searchUrl}?q=${encodeURIComponent(
        query
      )}&type=product${sortBy}`;
    }

    _debounce(fn, delay) {
      let timer = null;

      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }
  }

  // Exposed for assets/meddey-search.test.js (and for stores that want to plug in
  // their own algorithm: window.meddeySearchRanking.ALGORITHMS.myAlgo = fn).
  window.meddeySearchRanking = { tokenize, textScore, affinityScore, ALGORITHMS };

  if (!window.customElements.get('meddey-search')) {
    window.customElements.define('meddey-search', MeddeySearch);
  }
})();
